import React, { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { apiVerifyEmail, apiRequestSetPassword } from '../redux/AuthCRUD'
import { LoadingBlock } from '../../../staticComponents/LoadingBlock'
import { useIntl } from 'react-intl'


export function SetPassword() {
  const intl = useIntl()

  const initialValues = {
    password: '',
    confirmPassword: '',
  }

  const forgotPasswordSchema = Yup.object().shape({
    // password: Yup.string().required('Password is required'),
    password: Yup.string().required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { name: 'Password' })),
    confirmPassword: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { name: 'Confirmation password' }))
      .when('password', {
        is: (val: string) => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf([Yup.ref('password')], intl.formatMessage({ id: 'VALIDATION.DIDNT_MATCH.PASSWORD_AND_CONFIRM_PASSWORD' })),
      }),
  })

  const [loading, setLoading] = useState(false)
  const history = useNavigate()
  const [isVerifying, setIsVerifying] = useState<any>(true)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const email = queryParams.get('e')
    const token = queryParams.get('k')

    const data = {
      Email: email,
      Key: token,
    }

    apiVerifyEmail(data)
      .then((data) => {
        if (data.data && data.data.response === true) {
          setIsVerifying(false)
        } else {
          setIsVerifying(data.data.message)
        }
      })
      .catch((e) => {
        setIsVerifying('error')
      })
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        const queryParams: any = new URLSearchParams(window.location.search)
        const email = encodeURI(queryParams.get('e'))
        const token = encodeURI(queryParams.get('k'))

        const data = {
          Email: email,
          Key: token,
          Password: values.password,
        }

        apiRequestSetPassword(data)
          .then(({ data }) => {
            setHasErrors(false)
            setLoading(false)
            history('/auth/login')
          })
          .catch(() => {
            setLoading(false)
            setSubmitting(false)      
            setStatus(intl.formatMessage({ id: 'TOAST.ERROR_WHILE_SETTING_UP_PASSWORD' }))
          })
      }, 1000)
    },
  })

  if (isVerifying === true) {
    return (
      <>
        <div className='h-150px'>
          <LoadingBlock></LoadingBlock>
          <div className='text-center'>{intl.formatMessage({ id: 'AUTH.VERIFYING' })}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'>{intl.formatMessage({ id: 'AUTH.SET_PASSWORD' })}</h1>
          {/* end::Title */}

          {/* begin::Link */}
          {/* <div className='text-gray-400 fw-bold fs-4'>Enter your email to reset your password.</div> */}
          {/* end::Link */}
        </div>

        {isVerifying !== false && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{isVerifying}</div>
          </div>
        )}

        {isVerifying === false && (
          <div>
            {/* begin::Title */}
            {hasErrors === true && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>
                  {intl.formatMessage({ id: 'AUTH.SORRY_LOOKS_LIKE_THERE_ARE_SOME_ERRORS_DETECTED_PLEASE_TRY_AGAIN' })}
                </div>
              </div>
            )}

            {hasErrors === false && (
              <div className='mb-10 bg-light-info p-8 rounded'>
                <div className='text-info'>
                  {intl.formatMessage({ id: 'AUTH.SENT_PASSWORD_RESET._PLEASE_CHECK_YOUR_EMAIL' })}
                </div>
              </div>
            )}
            {/* end::Title */}

            {/* begin::Form group */}
            <div className='fv-row mb-10'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>{intl.formatMessage({ id: 'AUTH.INPUT.PASSWORD' })}</label>
              <input
                type='password'
                placeholder=''
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  { 'is-invalid': formik.touched.password && formik.errors.password },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-10'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>{intl.formatMessage({ id: 'AUTH.INPUT.CONFIRM_PASSWORD' })}</label>
              <input
                type='password'
                placeholder=''
                autoComplete='off'
                {...formik.getFieldProps('confirmPassword')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  { 'is-invalid': formik.touched.password && formik.errors.confirmPassword },
                  {
                    'is-valid': formik.touched.password && !formik.errors.confirmPassword,
                  }
                )}
              />
              {formik.touched.password && formik.errors.confirmPassword && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.confirmPassword}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              {!loading ? (
                <>
                  <button
                    type='submit'
                    id='kt_password_reset_submit'
                    className='btn btn-lg btn-primary fw-bolder me-4'
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({ id: 'AUTH.GENERAL.SUBMIT_BUTTON' })}
                    </span>
                  </button>
                  <Link to='/auth/login'>
                    <button
                      type='button'
                      id='kt_login_password_reset_form_cancel_button'
                      className='btn btn-lg btn-secondary fw-bolder'
                      disabled={formik.isSubmitting || !formik.isValid}
                    >
                      {intl.formatMessage({ id: 'BUTTON.CANCEL' })}
                    </button>
                  </Link>
                </>
              ) : (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {intl.formatMessage({ id: 'AUTH.GENERAL.PLEASE_WAIT' })}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* end::Form group */}
      </form>
    </>
  )
}
